import {
  CREATE_COSTCODE,
  CREATE_MANY_COSTCODE,
  DELETE_COSTCODE_BY_ID,
  UPDATE_COSTCODE,
  ARCHIVE_COSTCODE,
  GET_PHASES,
  MERGE_PHASES_FOR_PROJECT,
  GET_QUICKBOOKS_COSTCODES,
  GET_COSTCODE_CATEGORIES,
  CREATE_COSTCODE_TEMPLATE,
  GET_COSTCODE_TEMPLATES,
  UPDATE_COSTCODE_TEMPLATE,
  DELETE_COSTCODE_TEMPLATE_BY_ID,
  GET_COSTCODE_BILLING_DATA,
  GET_COSTCODE_CUSTOM_FIELD_TEMPLATE,
  UPDATE_COSTCODE_CUSTOM_FIELD_TEMPLATE,
  GET_COSTCODE_CUSTOM_DATA,
  GET_COSTCODE_CATEGORY_ASSIGNMENTS,
  MERGE_COSTCODE_ESTIMATES,
  CREATE_CUSTOM_COSTCODE_CATEGORY,
  UPDATE_CUSTOM_COSTCODE_CATEGORY,
  DELETE_CUSTOM_COSTCODE_CATEGORY,
  GET_SAGE_INTACCT_DATA,
  DELETE_PHASE_AND_COSTCODES,
  GET_COSTCODE_BY_IDS,
  GET_EXTERNAL_COSTCODES,
} from '../../state/actionTypes';

import { updateState, getIdMap, sortByCode } from '../../helpers/helpers';

const initialState = {
  costcodes: [],
  costcodeIdMap: {},
  phases: [],
  quickBooksCostcodes: [],
  categories: {},
  categoriesIdMap: {},
  costcodeBillingData: {},
  customFieldTemplate: {},
  customData: [],
  customDataFiles: {},
  categoryAssignments: {},
  sageIntacctCostcodes: [],
  externalCostcodes: [],
};

export default function costCodeActions(state = initialState, action = {}) {
  switch (action.type) {
    case CREATE_COSTCODE:
    case CREATE_MANY_COSTCODE: {
      const {
        payload: {
          costcodes = [],
        } = {},
      } = action;
      const {
        costcodes: stateCostcodes = [],
      } = state;
      const oldCC = stateCostcodes.slice();
      const costCodeSet = new Set(oldCC.map((cc) => cc.id));
      const newCostCodes = costcodes.map((cc) => ({
        ...cc,
        active: 1,
      })).filter((cc) => !costCodeSet.has(cc.id));
      const updatedCC = oldCC.concat(newCostCodes);
      updatedCC.sort(sortByCode('code'));

      const newCostcodeIdMap = getIdMap(updatedCC);

      return {
        ...state,
        costcodes: updatedCC,
        costcodeIdMap: newCostcodeIdMap,
      };
    }
    case GET_PHASES: {
      return {
        ...state,
        phases: action.payload.phases,
      };
    }
    case GET_EXTERNAL_COSTCODES: {
      const {
        costcodes: externalCostcodes = [],
      } = action.payload ?? {};
      return {
        ...state,
        externalCostcodes,
      };
    }
    case MERGE_PHASES_FOR_PROJECT: {
      const { projectId, phases: nextPhases } = action.payload;
      // Replace only the phase(s) we got back (by phase id) for this project, then concat.
      const phaseIdsToReplace = new Set((nextPhases || []).map((p) => p.id));
      const rest = (state.phases || []).filter(
        (p) => !(p.projectId === projectId && phaseIdsToReplace.has(p.id)),
      );
      const phases = rest.concat(nextPhases || []);
      return {
        ...state,
        phases,
      };
    }
    case DELETE_PHASE_AND_COSTCODES: {
      const { phaseId, removedCostcodeIds } = action.payload;
      let newPhases = state.phases;
      if (phaseId) {
        newPhases = (state.phases || []).filter((p) => p.id !== phaseId);
      }
      const costcodes = (state.costcodes || []).filter((cc) => !removedCostcodeIds.includes(cc.id));
      return {
        ...state,
        phases: newPhases,
        costcodes,
      };
    }
    case DELETE_COSTCODE_BY_ID: {
      const {
        costcodeIdMap: oldCostcodeIdMap,
      } = state;
      const newCostcodeIdMap = { ...oldCostcodeIdMap };
      delete newCostcodeIdMap[action.payload.deletedId];
      return {
        ...state,
        costcodes: state.costcodes.filter((cc) => cc.id !== action.payload.deletedId),
        costcodeIdMap: newCostcodeIdMap,
      };
    }
    case ARCHIVE_COSTCODE: {
      const {
        payload: {
          costcodeId,
          active,
        },
      } = action;
      return {
        ...state,
        costcodes: state.costcodes.map((cc) => {
          if (cc.id !== costcodeId) return cc;
          const newCC = { ...cc };
          newCC.active = active ? 1 : 0;
          return newCC;
        }),
      };
    }
    case UPDATE_COSTCODE: {
      const {
        payload: {
          costcode,
        },
      } = action;

      const {
        costcodeIdMap: oldCostcodeIdMap,
      } = state;
      const stateHasOldCostcode = costcode.id in oldCostcodeIdMap;
      const newCostcodeIdMap = stateHasOldCostcode
        ? { ...oldCostcodeIdMap }
        : oldCostcodeIdMap;
      if (stateHasOldCostcode) {
        newCostcodeIdMap[costcode.id] = {
          ...oldCostcodeIdMap[costcode.id],
          ...costcode,
        };
      }

      return {
        ...state,
        costcodes: updateState(state.costcodes, costcode),
        costcodeIdMap: newCostcodeIdMap,
      };
    }
    case GET_COSTCODE_BY_IDS: {
      const {
        payload: {
          data,
        } = {},
      } = action;
      const newCostcodeIdMap = { ...state.costcodeIdMap };
      data.forEach((costcode) => {
        newCostcodeIdMap[costcode.id] = {
          ...newCostcodeIdMap[costcode.id],
          ...costcode,
        };
      });
      return {
        ...state,
        costcodeIdMap: newCostcodeIdMap,
      };
    }
    case GET_QUICKBOOKS_COSTCODES: {
      const {
        data: quickBooksCostcodes,
      } = action.payload;
      return {
        ...state,
        quickBooksCostcodes,
      };
    }
    case GET_COSTCODE_CATEGORIES: {
      const {
        payload: {
          categories = [],
        } = {},
      } = action;
      return {
        ...state,
        categories: getIdMap(categories, 'name'),
        categoriesIdMap: getIdMap(categories, 'id'),
      };
    }
    case CREATE_COSTCODE_TEMPLATE: {
      const {
        payload: {
          template = {},
        } = {},
      } = action;
      const copiedTemplates = state.templates.slice();
      copiedTemplates.push(template);
      return {
        ...state,
        templates: copiedTemplates,
      };
    }
    case GET_COSTCODE_TEMPLATES: {
      const {
        payload: {
          templates = [],
        } = {},
      } = action;
      return {
        ...state,
        templates,
      };
    }
    case UPDATE_COSTCODE_TEMPLATE: {
      const {
        payload: {
          template = {},
        } = {},
      } = action;
      return {
        ...state,
        templates: updateState(state.templates, template),
      };
    }
    case DELETE_COSTCODE_TEMPLATE_BY_ID: {
      const {
        payload: {
          deletedId,
        } = {},
      } = action;
      return {
        ...state,
        templates: state.templates.filter((cc) => cc.id !== deletedId),
      };
    }
    case GET_COSTCODE_BILLING_DATA: {
      const {
        payload: {
          billingData = {},
        } = {},
      } = action;
      return {
        ...state,
        costcodeBillingData: billingData,
      };
    }
    case GET_COSTCODE_CUSTOM_FIELD_TEMPLATE:
    case UPDATE_COSTCODE_CUSTOM_FIELD_TEMPLATE: {
      const {
        payload: {
          template = {},
        } = {},
      } = action;
      return {
        ...state,
        customFieldTemplate: template,
      };
    }
    case GET_COSTCODE_CUSTOM_DATA: {
      const {
        data: {
          data = [],
          fileMap = {},
        } = {},
      } = action.payload;
      return {
        ...state,
        customData: data,
        customDataFiles: fileMap,
      };
    }
    case GET_COSTCODE_CATEGORY_ASSIGNMENTS: {
      const {
        payload: {
          data = {},
        } = {},
      } = action;

      const newCategoryAssignments = {
        ...state.categoryAssignments,
        ...data,
      };

      return {
        ...state,
        categoryAssignments: newCategoryAssignments,
      };
    }
    case MERGE_COSTCODE_ESTIMATES: {
      const {
        payload: {
          updatedCostcodes = [],
          editedPhaseId = null,
        } = {},
      } = action;
      if (updatedCostcodes.length === 0) return state;

      const updatedCostcodesMap = getIdMap(updatedCostcodes, 'id');

      const costcodesKey = editedPhaseId === null ? 'unphased' : editedPhaseId;
      const costCodeSet = new Set(state.costcodes.map((cc) => cc.id));
      const mergedCostcodes = state.costcodes.map((cc) => {
        const updated = updatedCostcodesMap[cc.id];
        if (!updated) return cc;
        const { categoryAssignments: _ca, ...rest } = updated;
        return { ...cc, ...rest };
      });

      const newCategoryAssignments = { ...state.categoryAssignments };

      updatedCostcodes.forEach((u) => {
        const {
          id: costcodeId,
          categoryAssignments: assignments,
          ...rest
        } = u;
        if (!costCodeSet.has(u.id)) {
          mergedCostcodes.push({ id: costcodeId, ...rest });
        }
        if (!assignments || typeof assignments !== 'object') return;
        newCategoryAssignments[costcodeId] = {
          ...newCategoryAssignments[costcodeId],
          [costcodesKey]: assignments,
        };
      });

      return {
        ...state,
        costcodes: mergedCostcodes,
        categoryAssignments: newCategoryAssignments,
      };
    }
    case CREATE_CUSTOM_COSTCODE_CATEGORY: {
      const {
        payload: {
          category = {},
        } = {},
      } = action;
      return {
        ...state,
        categories: {
          ...state.categories,
          [category.name]: category,
        },
        categoriesIdMap: {
          ...state.categoriesIdMap,
          [category.id]: category,
        },
      };
    }
    case UPDATE_CUSTOM_COSTCODE_CATEGORY: {
      const {
        payload: {
          category = {},
        } = {},
      } = action;

      // Remove old name key if name changed
      const oldCategory = state.categoriesIdMap[category.id];
      const newCategories = { ...state.categories };
      if (oldCategory && oldCategory.name !== category.name) {
        delete newCategories[oldCategory.name];
      }
      newCategories[category.name] = category;

      return {
        ...state,
        categories: newCategories,
        categoriesIdMap: {
          ...state.categoriesIdMap,
          [category.id]: category,
        },
      };
    }
    case DELETE_CUSTOM_COSTCODE_CATEGORY: {
      const {
        payload: {
          id,
        } = {},
      } = action;

      const deletedCategory = state.categoriesIdMap[id];
      const newCategories = { ...state.categories };
      const newCategoriesIdMap = { ...state.categoriesIdMap };

      if (deletedCategory) {
        delete newCategories[deletedCategory.name];
        delete newCategoriesIdMap[id];
      }

      return {
        ...state,
        categories: newCategories,
        categoriesIdMap: newCategoriesIdMap,
      };
    }
    case GET_SAGE_INTACCT_DATA: {
      const {
        payload: {
          data: {
            globalCostcodes = [],
          } = {},
        } = {},
      } = action;
      return {
        ...state,
        sageIntacctCostcodes: globalCostcodes,
      };
    }
    default:
      return state;
  }
}
